import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";

function PagesTab() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/pages").then(({ data }) => setPages(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put(`/admin/pages/${editing.slug}`, {
        title: editing.title,
        meta_description: editing.meta_description,
        h1: editing.h1,
        target_keyword: editing.target_keyword,
        status: editing.status,
        indexable: editing.indexable,
        ads_enabled: editing.ads_enabled,
      });
      toast.success("Page updated");
      setEditing(null);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div className="space-y-3">
      {pages.map((p) => (
        <div key={p.slug} className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`admin-page-${p.slug}`}>
          <div className="min-w-0">
            <div className="truncate font-medium">{p.title}</div>
            <div className="mt-0.5 text-xs text-muted-foreground">{p.path}</div>
            <div className="mt-1 flex flex-wrap gap-2 text-[11px]">
              <span className={`rounded px-1.5 py-0.5 ${p.indexable ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{p.indexable ? "indexable" : "noindex"}</span>
              <span className="rounded bg-secondary px-1.5 py-0.5">{p.status}</span>
              <span className={`rounded px-1.5 py-0.5 ${p.ads_enabled ? "bg-blue-100 text-blue-700" : "bg-secondary"}`}>ads {p.ads_enabled ? "on" : "off"}</span>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setEditing({ ...p })} data-testid={`admin-edit-${p.slug}`}>Edit</Button>
        </div>
      ))}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit page SEO</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Title tag</Label>
                <Input value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="edit-title" />
                <p className="text-xs text-muted-foreground">{(editing.title || "").length} chars</p>
              </div>
              <div className="space-y-1.5">
                <Label>Meta description</Label>
                <Textarea value={editing.meta_description || ""} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} data-testid="edit-desc" />
                <p className="text-xs text-muted-foreground">{(editing.meta_description || "").length} chars</p>
              </div>
              <div className="space-y-1.5">
                <Label>H1</Label>
                <Input value={editing.h1 || ""} onChange={(e) => setEditing({ ...editing, h1: e.target.value })} data-testid="edit-h1" />
              </div>
              <div className="space-y-1.5">
                <Label>Target keyword</Label>
                <Input value={editing.target_keyword || ""} onChange={(e) => setEditing({ ...editing, target_keyword: e.target.value })} />
              </div>
              <div className="flex items-center justify-between rounded-md border border-border p-3">
                <Label htmlFor="indexable">Indexable</Label>
                <Switch id="indexable" checked={!!editing.indexable} onCheckedChange={(v) => setEditing({ ...editing, indexable: v })} data-testid="edit-indexable" />
              </div>
              <div className="flex items-center justify-between rounded-md border border-border p-3">
                <Label htmlFor="ads">Ads enabled</Label>
                <Switch id="ads" checked={!!editing.ads_enabled} onCheckedChange={(v) => setEditing({ ...editing, ads_enabled: v })} data-testid="edit-ads" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={save} data-testid="admin-save-page">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FeesTab() {
  const [fees, setFees] = useState([]);
  const load = () => api.get("/fees").then(({ data }) => setFees(data));
  useEffect(() => { load(); }, []);

  const update = async (mp, patch) => {
    try {
      await api.put(`/admin/fees/${mp}`, patch);
      toast.success("Fee assumption updated");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Editable default assumptions. These are not official marketplace facts.</p>
      {fees.map((fee) => (
        <div key={fee.marketplace} className="rounded-lg border border-border bg-card p-4" data-testid={`admin-fee-${fee.marketplace}`}>
          <div className="font-medium">{fee.label}</div>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {["commission_pct", "payment_pct", "gst_pct", "fixed_fee"].map((k) => (
              <div key={k} className="space-y-1">
                <Label className="text-xs">{k}</Label>
                <Input
                  type="number"
                  defaultValue={fee[k]}
                  onBlur={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    if (val !== fee[k]) update(fee.marketplace, { [k]: val });
                  }}
                  data-testid={`fee-${fee.marketplace}-${k}`}
                />
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Last updated: {fee.updated_at}</p>
        </div>
      ))}
    </div>
  );
}

function AnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setData(data)); }, []);
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-border bg-card p-4"><div className="text-xs uppercase text-muted-foreground">Total events</div><div className="font-mono text-2xl font-semibold">{data.total_events}</div></div>
        <div className="rounded-lg border border-border bg-card p-4"><div className="text-xs uppercase text-muted-foreground">Saved calculations</div><div className="font-mono text-2xl font-semibold">{data.saved_calculations}</div></div>
      </div>
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="mb-2 text-sm font-semibold">Events by type</div>
        {data.by_event.length === 0 && <p className="text-sm text-muted-foreground">No events yet.</p>}
        {data.by_event.map((e) => (
          <div key={e.event} className="flex justify-between border-b border-border/50 py-1.5 text-sm last:border-0"><span>{e.event}</span><span className="font-mono">{e.count}</span></div>
        ))}
      </div>
    </div>
  );
}

function SeoAuditTab() {
  const [audit, setAudit] = useState(null);
  useEffect(() => { api.get("/admin/seo-audit").then(({ data }) => setAudit(data)); }, []);
  if (!audit) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {Object.entries(audit.scores).map(([k, v]) => (
          <div key={k} className="rounded-lg border border-border bg-card p-4 text-center">
            <div className="font-mono text-2xl font-semibold">{v}<span className="text-sm text-muted-foreground">/100</span></div>
            <div className="mt-1 text-xs uppercase text-muted-foreground">{k}</div>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-border bg-card p-4 text-sm">
        Indexable pages: <span className="font-mono font-semibold">{audit.indexable_pages}/{audit.total_pages}</span>
      </div>
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="mb-2 text-sm font-semibold">Issues ({audit.issues.length})</div>
        {audit.issues.length === 0 && <p className="text-sm text-emerald-600">No issues found. 🎉</p>}
        {audit.issues.map((i, idx) => (
          <div key={idx} className="flex items-start gap-2 border-b border-border/50 py-2 text-sm last:border-0">
            <span className={`mt-0.5 rounded px-1.5 py-0.5 text-[11px] ${i.severity === "high" ? "bg-red-100 text-red-700" : i.severity === "medium" ? "bg-amber-100 text-amber-700" : "bg-secondary"}`}>{i.severity}</span>
            <span><span className="font-mono text-xs">{i.slug}</span> — {i.issue}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">SEO & Content Admin</h1>
          <p className="text-sm text-muted-foreground">Signed in as {user?.email}</p>
        </div>
        <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout"><LogOut className="mr-2 h-4 w-4" />Log out</Button>
      </div>

      <Tabs defaultValue="pages" className="mt-8">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="pages" data-testid="tab-pages">Pages</TabsTrigger>
          <TabsTrigger value="fees" data-testid="tab-fees">Fee configs</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">SEO audit</TabsTrigger>
        </TabsList>
        <TabsContent value="pages" className="mt-6"><PagesTab /></TabsContent>
        <TabsContent value="fees" className="mt-6"><FeesTab /></TabsContent>
        <TabsContent value="analytics" className="mt-6"><AnalyticsTab /></TabsContent>
        <TabsContent value="audit" className="mt-6"><SeoAuditTab /></TabsContent>
      </Tabs>
    </div>
  );
}
